/**
 * Company facts on the brand page.
 *
 * A stubbed brand often has a name and nothing else. These fields stay blank
 * until someone fills them in; a brand the master list already knows arrives
 * with its website, headquarters and categories, and they can still be corrected.
 */

import React, { useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUnsavedChangesSource } from '../../../components/appUpdate';
import { ChipInput, SaveBar } from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import { brandManagementApi } from '../../../services/brandManagementApi';
import { normalizeUrl, validateUrl } from '../../../utils/inputValidators';
import { buildBrandCatalogProfile } from '../../../utils/brandCatalogProfile';
import { bvColor, bvType } from '../brandVendorsTokens';
import { regionsLabel, sellsLabel, websiteDomain, websiteHref } from '../brandLifecycle';
import { BvButton, BvCard, DefinitionRows } from './BrandVendorsPrimitives';

const CATEGORY_SUGGESTIONS = [
  'cameras', 'lenses', 'accessories', 'bags', 'tripods', 'filters', 'gimbals',
  'lighting', 'monitors', 'audio', 'drones', 'flash', 'batteries', 'straps',
];

export interface BrandCompanyFacts {
  id: string;
  name: string;
  website?: string | null;
  country?: string | null;
  categories?: string[];
  aliases?: string[];
  parentCompany?: string | null;
  regions?: string[];
}

interface Draft {
  website: string;
  country: string;
  categories: string[];
  aliases: string[];
}

const draftFrom = (brand: BrandCompanyFacts): Draft => ({
  website: brand.website || '',
  country: brand.country || '',
  categories: [...(brand.categories || [])],
  aliases: [...(brand.aliases || [])],
});

const sameList = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const categoryToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]+/g, '')
    .replace(/^_+|_+$/g, '');

const cleanList = (values: string[], normalize: (value: string) => string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const item = normalize(raw);
    const key = item.toLowerCase();
    if (!item || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const FactRow: React.FC<{ label: string; first?: boolean; children: React.ReactNode }> = ({
  label,
  first,
  children,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '108px 1fr' },
      gap: '12px',
      px: '13px',
      py: '9px',
      borderTop: first ? 'none' : `1px solid ${bvColor.borderSubtle}`,
      alignItems: 'start',
    }}
  >
    <Box sx={{ ...bvType.caption, color: bvColor.muted, pt: '8px' }}>{label}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

export const BrandCompanyCard: React.FC<{
  brand: BrandCompanyFacts;
  children?: React.ReactNode;
}> = ({ brand, children }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(brand));
  const [websiteError, setWebsiteError] = useState<string | null>(null);

  const saved = draftFrom(brand);
  const dirty =
    draft.website.trim() !== saved.website.trim() ||
    draft.country.trim() !== saved.country.trim() ||
    !sameList(draft.categories, saved.categories) ||
    !sameList(draft.aliases, saved.aliases);

  // A refetch while nothing is edited picks up the saved record. Edits in
  // progress are left alone.
  const savedKey = `${brand.id}|${saved.website}|${saved.country}|${saved.categories.join('\u0001')}|${saved.aliases.join('\u0001')}`;
  const [seenKey, setSeenKey] = useState(savedKey);
  if (savedKey !== seenKey) {
    setSeenKey(savedKey);
    if (!dirty) setDraft(saved);
  }

  const catalogProfile = buildBrandCatalogProfile(brand.id, brand.name, brand.categories);
  const stub = !brand.website && !brand.country && (brand.categories || []).length === 0;

  const save = useMutation({
    mutationFn: async () => {
      const website = draft.website.trim();
      const problem = validateUrl(website, { allowSchemeless: true });
      if (problem) {
        setWebsiteError(problem);
        throw new Error(problem);
      }
      const payload = {
        website: website ? normalizeUrl(website) : null,
        country: draft.country.trim() || null,
        categories: draft.categories,
        aliases: draft.aliases,
      };
      await brandManagementApi.updateBrand(brand.id, payload);
      return payload;
    },
    onSuccess: (payload) => {
      setDraft({
        website: payload.website || '',
        country: payload.country || '',
        categories: payload.categories,
        aliases: payload.aliases,
      });
      setWebsiteError(null);
      setEditing(false);
      showToast('Brand details saved.', 'success');
      queryClient.invalidateQueries({ queryKey: ['brand-directory-detail', brand.id] });
      queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
    },
  });

  const discard = () => {
    setDraft(saved);
    setWebsiteError(null);
    save.reset();
    setEditing(false);
  };

  useUnsavedChangesSource({
    dirty: editing && dirty,
    save: () => save.mutateAsync(),
    discard,
  });

  const websiteLink = websiteHref(brand.website);

  return (
    <BvCard
      title="Company"
      caption="Headquarters, catalog, and where they sell. A stub can stay sparse until you have the rest."
      action={
        editing ? (
          dirty ? null : (
            <BvButton size="sm" onClick={() => setEditing(false)}>
              Done
            </BvButton>
          )
        ) : (
          <BvButton size="sm" onClick={() => setEditing(true)}>
            Edit details
          </BvButton>
        )
      }
    >
      {stub && !editing && (
        <Box sx={{ ...bvType.caption, color: bvColor.muted, px: '13px', pt: '10px', pb: '2px' }}>
          This brand is a stub. Add a website, headquarters and what they sell when you have them.
        </Box>
      )}

      {editing ? (
        <>
          <FactRow label="Website" first>
            <TextField
              size="small"
              fullWidth
              label="Website"
              value={draft.website}
              error={Boolean(websiteError)}
              helperText={websiteError || 'Optional. https:// is added if you leave it off.'}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, website: event.target.value }));
                if (websiteError) setWebsiteError(null);
              }}
              onBlur={() => setWebsiteError(validateUrl(draft.website, { allowSchemeless: true }))}
            />
          </FactRow>
          <FactRow label="Headquarters">
            <TextField
              size="small"
              fullWidth
              label="Headquarters"
              placeholder="Country"
              value={draft.country}
              onChange={(event) => setDraft((prev) => ({ ...prev, country: event.target.value }))}
            />
          </FactRow>
          <FactRow label="Sells">
            <ChipInput
              aria-label="Categories"
              placeholder="Add a category"
              suggestions={CATEGORY_SUGGESTIONS}
              value={draft.categories}
              onChange={(values) => setDraft((prev) => ({ ...prev, categories: cleanList(values, categoryToken) }))}
            />
          </FactRow>
          <FactRow label="Aliases">
            <ChipInput
              aria-label="Aliases"
              placeholder="Other spellings on products"
              value={draft.aliases}
              onChange={(values) =>
                setDraft((prev) => ({ ...prev, aliases: cleanList(values, (value) => value.trim().replace(/\s+/g, ' ')) }))
              }
            />
          </FactRow>
        </>
      ) : (
        <DefinitionRows
          labelWidth={108}
          rows={[
            {
              label: 'Website',
              value: websiteLink ? (
                <Box
                  component="a"
                  href={websiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: bvColor.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {websiteDomain(brand.website)}
                </Box>
              ) : (
                '—'
              ),
            },
            { label: 'Headquarters', value: brand.country || '—' },
            ...(brand.parentCompany ? [{ label: 'Parent', value: brand.parentCompany }] : []),
            { label: 'Sells', value: sellsLabel(brand.categories) || catalogProfile?.sellsLabel || '—' },
            { label: 'Regions', value: regionsLabel(brand.regions) || '—' },
            { label: 'Aliases', value: (brand.aliases || []).filter(Boolean).join(', ') || '—' },
          ]}
        />
      )}

      {save.isError && (
        <Alert severity="error" sx={{ mx: '13px', mt: '10px' }}>
          {(save.error as Error).message || 'Could not save these details. Try again.'}
        </Alert>
      )}

      {children && (
        <Box sx={{ borderTop: `1px solid ${bvColor.borderSubtle}` }}>{children}</Box>
      )}

      <SaveBar
        dirty={editing && dirty}
        saving={save.isPending}
        message="Saving updates the directory. Aliases decide which product brand names belong here."
        onSave={() => save.mutate()}
        onDiscard={discard}
      />
    </BvCard>
  );
};
